/**
 * Creating a Flag from the new console's client record and Full Booking (same fields as the current console's Flag
 * Account dialog). Pure: what the staff member entered in, the flagged_accounts row out.
 *
 * A Flag belongs to a client (flagged_accounts.user_id) and a department; it may be given to one person in that
 * department. From a booking, the booking reference goes into the notes so the owner can find it. The database
 * trigger then tells the assigned person (New Flag Assigned).
 *
 * Traci's doc (Oct 2026, final word): creating a flag asks for the reason and the department only, no type, severity
 * or person; any team can flag the client or one of the booking's talent from the booking. A type, severity or
 * assignee sent by an older screen is still accepted: severity defaults to medium, and the type prefixes the reason.
 */

export const FLAG_TYPES = ['Payment Issue', 'Communication Issue', 'Cancellation', 'Refund Request', 'Complaint', 'Fraud Suspicion', 'Quality Issue', 'Other']
export const FLAG_SEVERITIES = ['low', 'medium', 'high', 'critical']
export const DEFAULT_FLAG_SEVERITY = 'medium'
export const FLAG_DEPARTMENTS = [
  { key: 'sales', label: 'Sales' },
  { key: 'billing', label: 'Billing' },
  { key: 'talent_coordinator', label: 'Talent' },
  { key: 'editing', label: 'Editing' },
  { key: 'admin', label: 'Admin' }
]

/**
 * @param input  { type, severity, reason, department, assignee_id?, client_id, booking_id?, booking_ref? }
 * @param ctx    { staff: [{ id, name, roles }], actorId }
 * @returns { ok, errors, row }
 */
export function planFlag(input = {}, { staff = [], actorId = null } = {}) {
  const errors = []
  const type = String(input.type || '').trim()
  const severity = String(input.severity || '').trim().toLowerCase() || DEFAULT_FLAG_SEVERITY
  const reason = String(input.reason || '').trim()
  const department = String(input.department || '')
  const clientId = String(input.client_id || '')
  if (type && !FLAG_TYPES.includes(type)) errors.push('Unknown flag type')
  if (reason.length < 3) errors.push('Say why you are flagging')
  if (!FLAG_SEVERITIES.includes(severity)) errors.push('Pick a severity')
  if (!FLAG_DEPARTMENTS.some(d => d.key === department)) errors.push('Pick a department')
  if (!clientId) errors.push('Pick who you are flagging')
  let assignee = null
  if (input.assignee_id) {
    assignee = staff.find(s => s.id === input.assignee_id) || null
    if (!assignee) errors.push('That person is not on the team')
    else if (!assignee.roles.includes(department) && !assignee.roles.includes('admin')) errors.push(`${assignee.name} is not in that department`)
  }
  const bookingRef = input.booking_ref ? String(input.booking_ref).slice(0, 12) : input.booking_id ? String(input.booking_id).slice(0, 8).toUpperCase() : null
  const notes = [bookingRef ? `Booking #${bookingRef}` : null, assignee ? `Assigned to: ${assignee.name}` : null].filter(Boolean).join(' · ') || null
  return {
    ok: errors.length === 0,
    errors,
    row: {
      user_id: clientId,
      severity,
      reason: type ? `${type}: ${reason.slice(0, 1000)}` : reason.slice(0, 1000),
      flagged_by: actorId,
      assigned_to_role: department,
      assigned_to: assignee ? assignee.id : null,
      status: 'open',
      notes
    }
  }
}
